// frontend/src/utils/relationships/buildRelationshipReferenceState.js
// Owns: active relationship-path evidence and unique relational reference resolution.
// Does not generate responses, write memory, or resolve ambiguous candidates.

import relationshipRoles from '../relationshipRoles';
import {
  getGraphRelationDefinition,
  getGraphRelationRoleOrientation,
  graphRelationMatchesRole,
} from './relationshipOntology';

const MEMORY_VERBS = new Set(['remember', 'recall', 'know']);
const RECALL_VERBS = new Set(['remember', 'recall']);
const QUESTION_OPENERS = new Set(['do', 'did', 'can', 'could', 'would', 'will']);
const POSSESSIVE_PRONOUNS = new Set(['my', 'our', 'her', 'his', 'their']);
const PET_ROLES = new Set(['dog', 'cat', 'pet']);

function normalizeText(value = '') {
  return String(value || '')
    .toLowerCase()
    .replace(/[‘’]/g, "'")
    .replace(/[-_]+/g, ' ')
    .replace(/[^a-z0-9'\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeName(value = '') {
  return normalizeText(value).replace(/'s$/g, '').trim();
}

function clampScore(value = 0) {
  return Math.max(0, Math.min(1, Number(Number(value || 0).toFixed(2))));
}

function getName(entity = {}) {
  return String(
    entity?.name ||
      [entity?.first_name || entity?.firstName, entity?.last_name || entity?.lastName]
        .filter(Boolean)
        .join(' ')
  ).trim();
}

function getRole(entity = {}) {
  return String(
    entity?.role ||
      entity?.relationship ||
      entity?.relationship_type ||
      entity?.relationshipType ||
      ''
  ).trim();
}

function getEntityId(entity = {}) {
  return String(entity?.entityId || entity?.metadata?.entityId || '').trim() || null;
}

function getEntityIdentityKey(entity = {}) {
  const entityId = getEntityId(entity);
  if (entityId) return `id:${entityId}`;

  const name = normalizeName(getName(entity));
  const role = canonicalRole(getRole(entity));
  if (!name) return '';

  return `legacy:${name}${role ? `:${role}` : ''}`;
}

function getRelationRole(edge = {}) {
  const metadataRole =
    edge?.metadata?.relationshipRole ||
    edge?.metadata?.legacyRelationship ||
    '';

  if (metadataRole) return canonicalRole(metadataRole);

  const definition = getGraphRelationDefinition(edge?.relation || '');
  return canonicalRole(definition?.requestRoles?.[0] || '');
}

function canonicalRole(value = '') {
  const normalized = normalizeText(value);
  if (!normalized) return '';

  const match = relationshipRoles.find((descriptor) => {
    const values = [
      descriptor?.keyword,
      descriptor?.role,
      ...(Array.isArray(descriptor?.aliases) ? descriptor.aliases : []),
    ]
      .map(normalizeText)
      .filter(Boolean);

    return values.includes(normalized);
  });

  return normalizeText(match?.keyword || match?.role || normalized);
}

function inferType(role = '', explicitType = '') {
  const type = normalizeText(explicitType);
  if (type && type !== 'unknown') return type;
  return PET_ROLES.has(canonicalRole(role)) ? 'animal' : 'person';
}

function tokenize(value = '') {
  const normalized = normalizeText(value);
  const tokens = [];
  const pattern = /[a-z0-9]+(?:'[a-z0-9]+)?/g;
  let match = pattern.exec(normalized);

  while (match) {
    tokens.push({ value: match[0], index: match.index });
    match = pattern.exec(normalized);
  }

  return { normalized, tokens };
}

function isRelationshipRecallLike(message = '') {
  const { tokens } = tokenize(message);
  const values = tokens.map((token) => token.value);
  if (!values.length) return false;

  const hasRecallVerb = values.some((value) => RECALL_VERBS.has(value));
  const hasKnow = values.includes('know');
  const hasSecondPerson = values.includes('you');
  const startsAsQuestion = QUESTION_OPENERS.has(values[0]);
  const hasQuestionMark = String(message || '').includes('?');

  return (
    (hasRecallVerb || (hasKnow && hasSecondPerson)) &&
    (hasSecondPerson || startsAsQuestion || hasQuestionMark)
  );
}

function getEdges(relationshipNetwork = null) {
  const edges = Array.isArray(relationshipNetwork)
    ? relationshipNetwork
    : Array.isArray(relationshipNetwork?.edges)
      ? relationshipNetwork.edges
      : [];

  return edges.filter((edge) => edge?.isActive !== false);
}

function buildKnownEntities(relationships = [], relationshipNetwork = null) {
  const edges = getEdges(relationshipNetwork);
  const entities = new Map();

  const add = (entity = {}, source = 'unknown', options = {}) => {
    const name = getName(entity);
    const entityId = getEntityId(entity);
    const incomingRole = getRole(entity) || options.role || null;
    const role = incomingRole || null;
    const key = entityId
      ? `id:${entityId}`
      : `legacy:${normalizeName(name)}:${canonicalRole(role)}`;

    if (!name || !key) return;

    const existing = entities.get(key) || {};
    const relationshipRoleEvidence = [
      ...(Array.isArray(existing.relationshipRoleEvidence)
        ? existing.relationshipRoleEvidence
        : []),
      incomingRole,
      ...(Array.isArray(options.relationshipRoleEvidence)
        ? options.relationshipRoleEvidence
        : []),
    ]
      .map(canonicalRole)
      .filter(Boolean);

    entities.set(key, {
      ...existing,
      ...entity,
      entityId: entityId || existing.entityId || null,
      name: existing.name || name,
      firstName:
        existing.firstName || entity?.first_name || entity?.firstName || name.split(' ')[0],
      role: existing.role || role || null,
      relationshipRoleEvidence: [...new Set(relationshipRoleEvidence)],
      type: inferType(role, existing.type || entity?.type || entity?.entityType),
      isFirstOrder: existing.isFirstOrder === true || options.isFirstOrder === true,
      sources: [...new Set([...(existing.sources || []), source])],
    });
  };

  // Graph identities are authoritative for identity. Root endpoints mark the
  // non-root endpoint as a first-order entity in the user's relational world.
  edges.forEach((edge) => {
    const relationRole = getRelationRole(edge);

    if (edge?.fromIsRoot !== true) {
      add(
        {
          entityId: edge?.fromEntityId || null,
          name: edge?.fromName,
          role: edge?.toIsRoot === true ? relationRole : null,
          type: edge?.fromType,
        },
        'relationship_network',
        {
          isFirstOrder: edge?.toIsRoot === true,
          relationshipRoleEvidence: edge?.toIsRoot === true ? [relationRole] : [],
        }
      );
    }

    if (edge?.toIsRoot !== true) {
      add(
        {
          entityId: edge?.toEntityId || null,
          name: edge?.toName,
          role: edge?.fromIsRoot === true ? relationRole : null,
          type: edge?.toType,
        },
        'relationship_network',
        {
          isFirstOrder: edge?.fromIsRoot === true,
          relationshipRoleEvidence: edge?.fromIsRoot === true ? [relationRole] : [],
        }
      );
    }
  });

  // Flat first-order records are compatibility projections during the cutover.
  // When an older record has no entityId, bind it to a unique matching root
  // graph edge rather than creating another name identity in working state.
  (Array.isArray(relationships) ? relationships : []).forEach((person) => {
    const personName = getName(person);
    const personRole = canonicalRole(getRole(person));
    let entityId = getEntityId(person);

    if (!entityId && personName) {
      const rootMatches = edges.filter((edge) => {
        const nonRootName = edge?.fromIsRoot === true ? edge?.toName : edge?.toIsRoot === true ? edge?.fromName : '';
        const nonRootEntityId = edge?.fromIsRoot === true ? edge?.toEntityId : edge?.toIsRoot === true ? edge?.fromEntityId : null;
        if (!nonRootEntityId || normalizeName(nonRootName) !== normalizeName(personName)) return false;
        return graphRelationMatchesRole(edge?.relation || '', personRole);
      });

      const matchingIds = [...new Set(rootMatches.map((edge) =>
        edge?.fromIsRoot === true ? edge?.toEntityId : edge?.fromEntityId
      ).filter(Boolean))];

      if (matchingIds.length === 1) entityId = matchingIds[0];
    }

    add(
      {
        ...person,
        entityId: entityId || null,
      },
      'relationship_memory',
      {
        isFirstOrder: true,
        relationshipRoleEvidence: [personRole],
      }
    );
  });

  return [...entities.values()];
}

function findEntityByName(value = '', entities = []) {
  const normalized = normalizeName(value);
  if (!normalized) return null;

  const exactMatches = entities.filter(
    (entity) => normalizeName(entity?.name) === normalized
  );

  if (exactMatches.length === 1) return exactMatches[0];

  if (exactMatches.length > 1) {
    const firstOrderMatches = exactMatches.filter((entity) => entity?.isFirstOrder === true);
    if (firstOrderMatches.length === 1) return firstOrderMatches[0];
    return null;
  }

  const firstNameMatches = entities.filter((entity) =>
    namesReferToSameEntity(normalized, entity?.name, entities)
  );

  if (firstNameMatches.length === 1) return firstNameMatches[0];

  const firstOrderMatches = firstNameMatches.filter((entity) => entity?.isFirstOrder === true);
  return firstOrderMatches.length === 1 ? firstOrderMatches[0] : null;
}

function getFirstName(value = '') {
  return normalizeName(value).split(' ')[0] || '';
}

function namesReferToSameEntity(left = '', right = '', entities = []) {
  const normalizedLeft = normalizeName(left);
  const normalizedRight = normalizeName(right);

  if (!normalizedLeft || !normalizedRight) return false;
  if (normalizedLeft === normalizedRight) return true;

  const leftFirst = getFirstName(normalizedLeft);
  const rightFirst = getFirstName(normalizedRight);

  if (!leftFirst || leftFirst !== rightFirst) return false;

  const leftIsFirstNameOnly = !normalizedLeft.includes(' ');
  const rightIsFirstNameOnly = !normalizedRight.includes(' ');

  if (!leftIsFirstNameOnly && !rightIsFirstNameOnly) return false;

  const competingFullNames = new Set(
    (Array.isArray(entities) ? entities : [])
      .map((entity) => normalizeName(entity?.name))
      .filter((name) => name && getFirstName(name) === leftFirst && name.includes(' '))
  );

  return competingFullNames.size <= 1;
}

function findNamedEntities(text = '', entities = []) {
  const normalized = normalizeText(text);
  if (!normalized) return [];

  return entities
    .map((entity) => {
      const names = [...new Set([
        normalizeName(entity?.name),
        normalizeName(entity?.firstName),
      ].filter(Boolean))].sort((a, b) => b.length - a.length);

      const matched = names.find((name) => {
        const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return new RegExp(`\\b${escaped}(?:'s|s)?\\b`, 'i').test(normalized);
      });

      if (!matched) return null;

      return {
        entity,
        index: normalized.indexOf(matched),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.index - b.index);
}

function selectNamedEntityMatches(matches = [], currentEntity = null) {
  if (!Array.isArray(matches) || matches.length <= 1) return matches || [];

  const currentEntityId = getEntityId(currentEntity || {});
  if (currentEntityId) {
    const currentMatch = matches.find(
      (match) => getEntityId(match?.entity || {}) === currentEntityId
    );
    if (currentMatch) return [currentMatch];
  }

  const groupedByName = matches.reduce((groups, match) => {
    const key = normalizeName(match?.entity?.name);
    if (!key) return groups;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(match);
    return groups;
  }, new Map());

  return [...groupedByName.values()].flatMap((group) => {
    if (group.length === 1) return group;
    const firstOrder = group.filter((match) => match?.entity?.isFirstOrder === true);
    return firstOrder.length === 1 ? firstOrder : [];
  });
}

function getLastQuestion(reply = '') {
  const sentences = String(reply || '')
    .split(/(?<=[.?!])\s+/)
    .map((part) => part.trim())
    .filter(Boolean);

  const questions = sentences.filter((sentence) => sentence.includes('?'));
  return questions[questions.length - 1] || '';
}

function mergeAnchor(anchors = [], entity = null, score = 0, source = '', questionTarget = false) {
  const name = getName(entity || {});
  const key = getEntityIdentityKey(entity || {});
  if (!key || !name) return anchors;

  const next = [...anchors];
  const index = next.findIndex((anchor) => getEntityIdentityKey(anchor?.entity || {}) === key);
  const normalizedEntity = {
    ...entity,
    name,
    role: getRole(entity || {}) || null,
    type: inferType(getRole(entity || {}), entity?.type),
  };

  if (index === -1) {
    next.push({
      entity: normalizedEntity,
      score: clampScore(score),
      sources: [source].filter(Boolean),
      questionTarget,
    });
    return next;
  }

  const existing = next[index];
  next[index] = {
    ...existing,
    entity: {
      ...existing.entity,
      ...normalizedEntity,
      role: existing.entity?.role || normalizedEntity.role,
    },
    score: Math.max(existing.score || 0, score || 0),
    sources: [...new Set([...(existing.sources || []), source].filter(Boolean))],
    questionTarget: existing.questionTarget || questionTarget,
  };

  return next;
}

function buildAnchors({
  message = '',
  lastAssistantMessage = '',
  currentEntity = null,
  entityState = null,
  entities = [],
} = {}) {
  let anchors = [];

  selectNamedEntityMatches(
    findNamedEntities(message, entities),
    currentEntity || entityState?.active
  ).forEach(({ entity }) => {
    anchors = mergeAnchor(anchors, entity, 1, 'current_message');
  });

  selectNamedEntityMatches(
    findNamedEntities(getLastQuestion(lastAssistantMessage), entities),
    currentEntity || entityState?.active
  ).forEach(({ entity }) => {
    anchors = mergeAnchor(anchors, entity, 0.95, 'assistant_question', true);
  });

  selectNamedEntityMatches(
    findNamedEntities(lastAssistantMessage, entities),
    currentEntity || entityState?.active
  ).forEach(({ entity }) => {
    anchors = mergeAnchor(anchors, entity, 0.72, 'assistant_reply');
  });

  if (currentEntity) {
    anchors = mergeAnchor(anchors, currentEntity, 0.82, 'current_entity');
  }

  [
    entityState?.active,
    entityState?.previous,
    ...(Array.isArray(entityState?.named) ? entityState.named : []),
    ...(Array.isArray(entityState?.related) ? entityState.related : []),
  ]
    .filter(Boolean)
    .forEach((entity) => {
      anchors = mergeAnchor(anchors, entity, 0.58, 'entity_state');
    });

  return anchors.sort((a, b) => (b.score || 0) - (a.score || 0));
}

function endpointMatchesEntity({ entity = null, endpointName = '', endpointEntityId = null, entities = [] } = {}) {
  const entityId = getEntityId(entity || {});
  const candidateEndpointId = String(endpointEntityId || '').trim();

  if (entityId && candidateEndpointId) return entityId === candidateEndpointId;
  return namesReferToSameEntity(getName(entity || {}), endpointName, entities);
}

function expandAnchorsThroughRelationshipNetwork({
  anchors = [],
  relationshipNetwork = null,
  entities = [],
} = {}) {
  const baseAnchors = Array.isArray(anchors) ? [...anchors] : [];
  let expanded = [...baseAnchors];

  baseAnchors.forEach((anchor) => {
    if (!anchor?.entity) return;

    getEdges(relationshipNetwork).forEach((edge) => {
      const fromMatches = endpointMatchesEntity({
        entity: anchor.entity,
        endpointName: edge?.fromName,
        endpointEntityId: edge?.fromEntityId,
        entities,
      });
      const toMatches = endpointMatchesEntity({
        entity: anchor.entity,
        endpointName: edge?.toName,
        endpointEntityId: edge?.toEntityId,
        entities,
      });

      if (!fromMatches && !toMatches) return;

      const neighborName = fromMatches ? edge?.toName : edge?.fromName;
      const neighborEntityId = fromMatches ? edge?.toEntityId : edge?.fromEntityId;
      const neighbor =
        entities.find((entity) => getEntityId(entity) && getEntityId(entity) === neighborEntityId) ||
        findEntityByName(neighborName, entities) ||
        {
          entityId: neighborEntityId || null,
          name: neighborName,
          type: fromMatches ? edge?.toType : edge?.fromType,
        };

      const edgeConfidence = Number.isFinite(Number(edge?.confidence))
        ? Number(edge.confidence)
        : 1;
      const score = clampScore((anchor?.score || 0) * 0.78 * edgeConfidence);

      expanded = mergeAnchor(
        expanded,
        neighbor,
        score,
        'relationship_network_neighbor'
      );
    });
  });

  return expanded.sort((a, b) => (b.score || 0) - (a.score || 0));
}

function buildRoleVocabulary() {
  return relationshipRoles
    .flatMap((descriptor) => {
      const role = canonicalRole(descriptor?.keyword || descriptor?.role || '');
      return [
        descriptor?.keyword,
        descriptor?.role,
        ...(Array.isArray(descriptor?.aliases) ? descriptor.aliases : []),
      ]
        .map(normalizeText)
        .filter(Boolean)
        .map((alias) => ({
          alias,
          role,
          words: alias.split(' ').filter(Boolean),
        }));
    })
    .sort((a, b) => {
      if (b.words.length !== a.words.length) return b.words.length - a.words.length;
      return b.alias.length - a.alias.length;
    });
}

const ROLE_VOCABULARY = buildRoleVocabulary();

function roleTokenMatches(tokenValue = '', vocabularyWord = '') {
  const token = normalizeText(tokenValue);
  const word = normalizeText(vocabularyWord);

  if (!token || !word) return false;
  if (token === word) return true;

  // Token-level morphology only. This lets ordinary possessive/plural speech
  // such as "mom's", "moms", "dad's", and "dads" resolve through
  // the same relationship vocabulary without sentence-pattern parsing.
  if (token.endsWith("'s") && token.slice(0, -2) === word) return true;
  if (token.length > word.length && token.endsWith('s') && token.slice(0, -1) === word) {
    return true;
  }

  return false;
}

function extractRoleMentions(message = '') {
  const { tokens } = tokenize(message);
  const mentions = [];

  ROLE_VOCABULARY.forEach(({ alias, role, words }) => {
    if (!words.length || words.length > tokens.length) return;

    for (let tokenIndex = 0; tokenIndex <= tokens.length - words.length; tokenIndex += 1) {
      const matches = words.every((word, offset) =>
        roleTokenMatches(tokens[tokenIndex + offset]?.value, word)
      );

      if (!matches) continue;

      mentions.push({
        role,
        alias,
        index: tokens[tokenIndex].index,
        tokenIndex,
        possessor: findPossessor(tokens, tokenIndex),
      });
    }
  });

  const seen = new Set();
  return mentions
    .sort((a, b) => a.index - b.index || b.alias.length - a.alias.length)
    .filter((mention) => {
      const key = `${mention.index}:${mention.role}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function findPossessor(tokens = [], roleTokenIndex = -1) {
  const preceding = tokens.slice(Math.max(0, roleTokenIndex - 4), roleTokenIndex);

  for (let index = preceding.length - 1; index >= 0; index -= 1) {
    const token = preceding[index]?.value || '';

    if (POSSESSIVE_PRONOUNS.has(token)) {
      return {
        type: token === 'my' || token === 'our' ? 'user' : 'pronoun',
        value: token,
      };
    }

    if (token.endsWith("'s")) {
      return { type: 'name', value: token.slice(0, -2) };
    }
  }

  return null;
}

function genderFor(entity = null) {
  const roles = [
    getRole(entity || {}),
    ...(Array.isArray(entity?.relationshipRoleEvidence)
      ? entity.relationshipRoleEvidence
      : []),
  ]
    .map(canonicalRole)
    .filter(Boolean);

  const genders = new Set(
    roles
      .map((role) => {
        const descriptor = relationshipRoles.find((item) => {
          return canonicalRole(item?.keyword || item?.role || '') === role;
        });

        return descriptor?.gender || 'unknown';
      })
      .filter((gender) => gender === 'male' || gender === 'female')
  );

  return genders.size === 1 ? [...genders][0] : 'unknown';
}

function isPerson(entity = null) {
  const type = normalizeText(entity?.type);
  const role = canonicalRole(getRole(entity || {}));
  if (type === 'animal' || type === 'pet' || PET_ROLES.has(role)) return false;
  return true;
}

function resolvePronounOwner(pronoun = '', anchors = []) {
  let candidates = [...anchors];

  if (pronoun === 'her') {
    candidates = candidates.filter((anchor) => {
      if (!isPerson(anchor.entity)) return false;
      return genderFor(anchor.entity) !== 'male';
    });
  } else if (pronoun === 'his') {
    candidates = candidates.filter((anchor) => {
      if (!isPerson(anchor.entity)) return false;
      return genderFor(anchor.entity) !== 'female';
    });
  }

  candidates.sort((a, b) => (b.score || 0) - (a.score || 0));
  if (candidates.length === 1) return candidates[0].entity;
  if (!candidates.length) return null;

  return (candidates[0].score || 0) - (candidates[1].score || 0) >= 0.12
    ? candidates[0].entity
    : null;
}

function resolvePossessor(possessor = null, anchors = [], entities = []) {
  if (!possessor) return { type: 'none', entity: null };
  if (possessor.type === 'user') return { type: 'user', entity: null };

  if (possessor.type === 'name') {
    const entity = findEntityByName(possessor.value, entities);
    return entity ? { type: 'entity', entity } : { type: 'none', entity: null };
  }

  if (possessor.type === 'pronoun') {
    const entity = resolvePronounOwner(possessor.value, anchors);
    return entity ? { type: 'entity', entity } : { type: 'none', entity: null };
  }

  return { type: 'none', entity: null };
}

function relationMatchesRole(relation = '', role = '') {
  return graphRelationMatchesRole(relation, canonicalRole(role));
}

function buildResolvedEntity(name = '', role = '', type = '', metadata = {}, entityId = null) {
  if (!name) return null;

  return {
    entityId: String(entityId || metadata?.entityId || '').trim() || null,
    type: inferType(role, type),
    name,
    firstName: String(name).split(' ')[0] || null,
    role: canonicalRole(role),
    source: 'relationship_reference_state',
    roles: ['active', 'related'],
    signals: {
      resolved: true,
      relationallyResolved: true,
      related: true,
    },
    metadata,
  };
}

function resolveRoleTargets({
  role = '',
  possessorResolution = null,
  anchors = [],
  entities = [],
  relationships = [],
  relationshipNetwork = null,
} = {}) {
  let targets = [];

  if (possessorResolution?.type === 'user') {
    targets = (Array.isArray(relationships) ? relationships : [])
      .filter((person) => canonicalRole(getRole(person)) === canonicalRole(role))
      .map((person) =>
        buildResolvedEntity(
          getName(person),
          role,
          person?.type,
          { pathType: 'direct_relationship_role' },
          getEntityId(person)
        )
      )
      .filter(Boolean);
  } else {
    const edges = getEdges(relationshipNetwork);
    const owners = possessorResolution?.entity
      ? [possessorResolution.entity]
      : anchors.map((anchor) => anchor?.entity).filter(Boolean);

    targets = edges
      .filter((edge) => relationMatchesRole(edge?.relation, role))
      .flatMap((edge) => {
        const roleOrientation = getGraphRelationRoleOrientation(edge?.relation, role);
        if (!roleOrientation) return [];

        const ownerMatchesTo = owners.some((owner) => endpointMatchesEntity({
          entity: owner,
          endpointName: edge?.toName,
          endpointEntityId: edge?.toEntityId,
          entities,
        }));
        const ownerMatchesFrom = owners.some((owner) => endpointMatchesEntity({
          entity: owner,
          endpointName: edge?.fromName,
          endpointEntityId: edge?.fromEntityId,
          entities,
        }));

        const resolvedTargets = [];

        // Directed relation roles describe a specific endpoint. For example,
        // Gene --father_of--> Carole means Gene is Carole's father; it does not
        // mean Carole is Gene's father. Reciprocal relations may resolve either side.
        if ((roleOrientation === 'from' || roleOrientation === 'either') && ownerMatchesTo) {
          const target = buildResolvedEntity(
            edge?.fromName,
            role,
            edge?.fromType,
            {
              ownerName: edge?.toName,
              ownerEntityId: edge?.toEntityId || null,
              relation: edge?.relation,
              pathType: 'relationship_network_edge',
              edgeOrientation: 'target_to_owner',
            },
            edge?.fromEntityId || null
          );
          if (target) resolvedTargets.push(target);
        }

        if ((roleOrientation === 'to' || roleOrientation === 'either') && ownerMatchesFrom) {
          const target = buildResolvedEntity(
            edge?.toName,
            role,
            edge?.toType,
            {
              ownerName: edge?.fromName,
              ownerEntityId: edge?.fromEntityId || null,
              relation: edge?.relation,
              pathType: 'relationship_network_edge',
              edgeOrientation: 'owner_to_target',
            },
            edge?.toEntityId || null
          );
          if (target) resolvedTargets.push(target);
        }

        return resolvedTargets;
      });

    if (!targets.length && possessorResolution?.type !== 'entity') {
      targets = (Array.isArray(relationships) ? relationships : [])
        .filter((person) => canonicalRole(getRole(person)) === canonicalRole(role))
        .map((person) =>
          buildResolvedEntity(
            getName(person),
            role,
            person?.type,
            { pathType: 'direct_relationship_role' },
            getEntityId(person)
          )
        )
        .filter(Boolean);
    }
  }

  const byIdentity = new Map();
  targets.forEach((target) => {
    const key = getEntityIdentityKey(target);
    if (key && !byIdentity.has(key)) byIdentity.set(key, target);
  });

  return [...byIdentity.values()];
}

function buildRoleResolutions({
  message = '',
  anchors = [],
  entities = [],
  relationships = [],
  relationshipNetwork = null,
} = {}) {
  const { tokens } = tokenize(message);
  const memoryVerbIndexes = tokens
    .map((token, index) => (MEMORY_VERBS.has(token.value) ? index : -1))
    .filter((index) => index >= 0);

  return extractRoleMentions(message).map((mention) => {
    const possessorResolution = resolvePossessor(mention.possessor, anchors, entities);
    const candidates = resolveRoleTargets({
      role: mention.role,
      possessorResolution,
      anchors,
      entities,
      relationships,
      relationshipNetwork,
    });

    const followsMemoryVerb = memoryVerbIndexes.some(
      (index) => mention.tokenIndex > index
    );

    const resolvedEntity = candidates.length === 1 ? candidates[0] : null;
    let score = resolvedEntity ? 0.42 : 0;
    if (mention.possessor) score += 0.2;
    if (followsMemoryVerb) score += 0.2;
    if (possessorResolution.type === 'entity') score += 0.12;
    if (resolvedEntity?.metadata?.relation) score += 0.08;

    return {
      ...mention,
      possessorEntity: possessorResolution.entity || null,
      candidates,
      resolvedEntity,
      followsMemoryVerb,
      score: clampScore(score),
    };
  });
}

function selectBestResolvedResolution(resolutions = []) {
  const resolved = resolutions.filter((item) => item?.resolvedEntity);
  if (!resolved.length) return null;

  const afterMemoryVerb = resolved
    .filter((item) => item.followsMemoryVerb)
    .sort((a, b) => a.index - b.index || b.score - a.score);

  if (afterMemoryVerb.length) return afterMemoryVerb[0];

  return [...resolved].sort((a, b) => b.score - a.score || a.index - b.index)[0];
}

function getRequestBoundary(message = '') {
  const { normalized, tokens } = tokenize(message);
  const memoryVerbTokenIndex = tokens.findIndex((token) =>
    MEMORY_VERBS.has(token.value)
  );

  if (memoryVerbTokenIndex < 0) {
    return {
      memoryVerb: null,
      memoryVerbTokenIndex: -1,
      characterIndex: -1,
      precedingSegment: '',
      requestSegment: normalized,
    };
  }

  const characterIndex = tokens[memoryVerbTokenIndex]?.index ?? -1;

  return {
    memoryVerb: tokens[memoryVerbTokenIndex]?.value || null,
    memoryVerbTokenIndex,
    characterIndex,
    precedingSegment:
      characterIndex >= 0 ? normalized.slice(0, characterIndex).trim() : '',
    requestSegment:
      characterIndex >= 0 ? normalized.slice(characterIndex).trim() : normalized,
  };
}

function selectRequestedResolution(
  resolutions = [],
  memoryVerbTokenIndex = -1
) {
  if (memoryVerbTokenIndex < 0) return null;

  const afterMemoryVerb = resolutions
    .filter((item) => item?.tokenIndex > memoryVerbTokenIndex)
    .sort((a, b) => a.tokenIndex - b.tokenIndex || a.index - b.index);

  return afterMemoryVerb[0] || null;
}

function buildPronounReferences(message = '', anchors = []) {
  const { tokens } = tokenize(message);
  const questionTarget = anchors.find((anchor) => anchor.questionTarget);

  return tokens
    .filter((token) => ['he', 'him', 'she', 'it'].includes(token.value))
    .map((token) => ({
      token: token.value,
      resolvedEntity: questionTarget?.entity || null,
      source: questionTarget ? 'assistant_question_target' : 'unresolved',
    }));
}

function buildRelationshipReferenceState({
  message = '',
  lastAssistantMessage = '',
  currentEntity = null,
  entityState = null,
  relationships = [],
  relationshipNetwork = null,
} = {}) {
  const requestBoundary = getRequestBoundary(message);
  const entities = buildKnownEntities(relationships, relationshipNetwork);
  const directAnchors = buildAnchors({
    message,
    lastAssistantMessage,
    currentEntity,
    entityState,
    entities,
  });

  const anchors = expandAnchorsThroughRelationshipNetwork({
    anchors: directAnchors,
    relationshipNetwork,
    entities,
  });

  const roleResolutions = buildRoleResolutions({
    message,
    anchors,
    entities,
    relationships,
    relationshipNetwork,
  });

  const recallLike = isRelationshipRecallLike(message);
  const requestedResolution = selectRequestedResolution(
    roleResolutions,
    requestBoundary.memoryVerbTokenIndex
  );

  const primaryResolution = requestedResolution
    ? requestedResolution
    : selectBestResolvedResolution(roleResolutions);

  const confidence = primaryResolution?.score || 0;
  const resolved = Boolean(primaryResolution?.resolvedEntity && confidence >= 0.7);

  const requestRoleResolutions = roleResolutions.filter(
    (item) => item?.tokenIndex > requestBoundary.memoryVerbTokenIndex
  );

  const nestedResolvedEntities = requestRoleResolutions
    .filter((item) => item !== requestedResolution && item?.resolvedEntity)
    .map((item) => item.resolvedEntity);

  const supportingEntities = [
    ...anchors.map((anchor) => anchor.entity),
    ...roleResolutions.map((resolution) => resolution.possessorEntity),
    ...nestedResolvedEntities,
  ]
    .filter(Boolean)
    .filter(
      (entity, index, list) =>
        list.findIndex((item) => getEntityIdentityKey(item) === getEntityIdentityKey(entity)) === index
    )
    .filter(
      (entity) => getEntityIdentityKey(entity) !== getEntityIdentityKey(primaryResolution?.resolvedEntity || {})
    );

  return {
    resolved,
    recallLike,
    shouldControlIntent: Boolean(recallLike && requestedResolution),
    primaryEntity: resolved ? primaryResolution.resolvedEntity : null,
    targetRole: primaryResolution?.role || null,
    requestedRole: requestedResolution?.role || null,
    requestedResolution: requestedResolution || null,
    requestRoleResolutions,
    requestBoundary,
    confidence: clampScore(confidence),
    anchors,
    supportingEntities,
    roleResolutions,
    pronounReferences: buildPronounReferences(message, anchors),
    evidence: {
      memoryVerbDetected: recallLike,
      activeAnchorCount: anchors.length,
      relationshipMentionCount: roleResolutions.length,
      resolvedRelationshipCount: roleResolutions.filter((item) => item.resolvedEntity).length,
      primaryPathType: primaryResolution?.resolvedEntity?.metadata?.pathType || null,
      requestedRole: requestedResolution?.role || null,
      requestedRoleResolved: Boolean(requestedResolution?.resolvedEntity),
      nestedResolvedCount: nestedResolvedEntities.length,
      preventedNestedTargetSubstitution: Boolean(
        recallLike &&
          requestedResolution &&
          !requestedResolution.resolvedEntity &&
          nestedResolvedEntities.length
      ),
    },
    reason: resolved
      ? `Resolved ${primaryResolution.role} to ${primaryResolution.resolvedEntity.name} through the active relationship frame.`
      : requestedResolution
        ? `The requested ${requestedResolution.role} relationship did not acquire enough evidence to control the turn; later nested relationship mentions were not promoted over it.`
        : 'No unique relationship target acquired enough evidence to control the turn.',
  };
}

export { buildRelationshipReferenceState };
export default buildRelationshipReferenceState;
